import type { CandidateTimeline } from "@shared/labs/level-assessment-candidate";
import { useLayoutEffect, useRef, useState } from "react";
import { C, MONO, UI } from "../../results/ui-tokens";
import { Card } from "../../sets/ui";
import { type ConvergencePoint, convergenceSeries } from "./derive";

/**
 * Convergência — the measurement-at-work chart: the overall estimate per round
 * on the level scale, with the ± band showing uncertainty being bought down
 * (or not) as evidence accumulates. Filled markers = the round re-scored;
 * hollow = it carried the previous estimate. Confidence is annotated under
 * each round.
 */

const PAD_L = 34;
const PAD_R = 16;
const PAD_T = 14;
const PAD_B = 44;
const HEIGHT = 240;

export function ConvergenceLens({ data }: { data: CandidateTimeline }) {
  const points = convergenceSeries(data.rounds);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [measured, setMeasured] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const update = () => setMeasured(el.clientWidth || null);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (points.length === 0) {
    return (
      <Card style={{ padding: 24, marginTop: 14 }}>
        <span style={{ ...UI, fontSize: 13.5, color: C.ink50 }}>
          Sem estimativa ainda. A primeira rodada avaliada inicia a trajetória.
        </span>
      </Card>
    );
  }

  const width = Math.max(360, measured ?? 920);
  const { min, max } = data.levels;
  const span = Math.max(1, max - min);
  const innerW = width - PAD_L - PAD_R;
  const innerH = HEIGHT - PAD_T - PAD_B;
  const y = (level: number) => PAD_T + innerH - ((clamp(level, min, max) - min) / span) * innerH;
  // A single point centers; N points spread across the width.
  const x = (i: number) =>
    points.length === 1 ? PAD_L + innerW / 2 : PAD_L + (i / (points.length - 1)) * innerW;

  const banded = points.filter((p) => p.mean !== null && p.stddev !== null);
  const bandPath =
    banded.length >= 2
      ? `M ${banded
          .map((p) => `${x(points.indexOf(p))},${y((p.mean as number) + (p.stddev as number))}`)
          .join(" L ")} L ${banded
          .slice()
          .reverse()
          .map((p) => `${x(points.indexOf(p))},${y((p.mean as number) - (p.stddev as number))}`)
          .join(" L ")} Z`
      : null;
  const meanPts = points.filter((p) => p.mean !== null);
  const meanLine =
    meanPts.length >= 2
      ? meanPts.map((p) => `${x(points.indexOf(p))},${y(p.mean as number)}`).join(" ")
      : null;

  const levelTicks: number[] = [];
  for (let n = min; n <= max; n++) levelTicks.push(n);

  return (
    <Card style={{ padding: "20px 22px", marginTop: 14 }}>
      <div ref={hostRef}>
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label="Trajetória da estimativa por rodada"
        >
          {/* Level gridlines */}
          {levelTicks.map((n) => (
            <g key={n}>
              <line
                x1={PAD_L}
                x2={width - PAD_R}
                y1={y(n)}
                y2={y(n)}
                stroke={C.ink10}
                strokeWidth={1}
              />
              <text
                x={PAD_L - 8}
                y={y(n) + 3.5}
                textAnchor="end"
                style={{ ...MONO, fontSize: 11, fill: C.ink50 }}
              >
                L{n}
              </text>
            </g>
          ))}

          {/* Uncertainty band */}
          {bandPath && <path d={bandPath} fill={C.red12} stroke="none" />}
          {/* Mean line */}
          {meanLine && <polyline points={meanLine} fill="none" stroke={C.red} strokeWidth={2} />}

          {/* Round markers + x labels */}
          {points.map((p, i) => (
            <g key={p.roundId}>
              {p.mean !== null && (
                <circle
                  cx={x(i)}
                  cy={y(p.mean)}
                  r={4.5}
                  fill={p.scored ? C.red : C.white}
                  stroke={C.red}
                  strokeWidth={2}
                />
              )}
              <text
                x={x(i)}
                y={HEIGHT - PAD_B + 18}
                textAnchor="middle"
                style={{ ...MONO, fontSize: 11, fill: C.ink70 }}
              >
                {`R${p.sequence}`}
              </text>
              <text
                x={x(i)}
                y={HEIGHT - PAD_B + 32}
                textAnchor="middle"
                style={{ ...MONO, fontSize: 10.5, fill: C.ink50 }}
              >
                {p.confidence === null ? "—" : `${Math.round(p.confidence * 100)}%`}
              </text>
            </g>
          ))}
        </svg>
      </div>

      {/* Reading key: plain words, no chrome */}
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 6 }}>
        <KeyItem swatch="filled" text="rodada avaliada" />
        <KeyItem swatch="hollow" text="nível herdado" />
        <KeyItem swatch="band" text="banda de incerteza (± desvio)" />
        <span style={{ ...MONO, fontSize: 11.5, color: C.ink50, marginLeft: "auto" }}>
          % = confiança da rodada
        </span>
      </div>

      <RoundLegend points={points} />
    </Card>
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function KeyItem({ swatch, text }: { swatch: "filled" | "hollow" | "band"; text: string }) {
  return (
    <span
      style={{
        ...UI,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11.5,
        color: C.ink50,
      }}
    >
      {swatch === "band" ? (
        <span style={{ width: 14, height: 8, background: C.red12, borderRadius: 2 }} />
      ) : (
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: swatch === "filled" ? C.red : C.white,
            border: `2px solid ${C.red}`,
          }}
        />
      )}
      {text}
    </span>
  );
}

/** R1/R2… → the round's own label, so the axis stays compact but nothing is
 *  lost. One line, wraps freely. */
function RoundLegend({ points }: { points: ConvergencePoint[] }) {
  const labeled = points.filter((p) => p.label !== `Rodada ${p.sequence}`);
  if (labeled.length === 0) return null;
  return (
    <div style={{ ...UI, fontSize: 11.5, color: C.ink50, marginTop: 8, lineHeight: 1.6 }}>
      {labeled.map((p, i) => (
        <span key={p.roundId}>
          {i > 0 && " · "}
          <span style={{ ...MONO }}>R{p.sequence}</span> {p.label}
        </span>
      ))}
    </div>
  );
}
